import { DateHelper, IDateTimeType } from '@connect-soft/date-helper';
import { TimePicker as MuiTimePicker, TimePickerProps, TimeValidationError } from '@mui/x-date-pickers';
import { forwardRef, useCallback } from 'react';
import { ITextFieldProps } from '../TextField';
import TimePickerErrorMessages from './TimePickerErrorMessages';

export type ITimePickerProps = Omit<
    TimePickerProps<IDateTimeType>,
    'defaultValue' | 'label' | 'minTime' | 'maxTime' | 'name' | 'value' | 'onError' | 'onChange' | 'renderInput'
> &
    Pick<
        ITextFieldProps,
        'enableDOMAttributeRequired' | 'error' | 'helperText' | 'hidden' | 'label' | 'name' | 'required' | 'size'
    > & {
        maxTime?: TimePickerProps<IDateTimeType>['maxTime'] | Date;
        minTime?: TimePickerProps<IDateTimeType>['minTime'] | Date;
        value?: TimePickerProps<IDateTimeType>['value'] | Date;
        onError?: (message: string) => void;
        onChange?: (value: IDateTimeType | null) => void;
    };

export const TimePicker = forwardRef<HTMLDivElement, ITimePickerProps>(function InnerTimePicker(
    {
        // inputFormat = TIME_INPUT_FORMAT,
        disabled,
        enableDOMAttributeRequired,
        error,
        helperText,
        hidden,
        label,
        maxTime,
        minTime,
        name,
        readOnly,
        required,
        size = 'small',
        value,
        onError,
        onChange,
        ...rest
    },
    ref
) {
    const handleError = useCallback(
        (reason: TimeValidationError) => {
            if (!value) {
                return;
            }

            if (onError) {
                onError(TimePickerErrorMessages(reason));
            }
        },
        [onError, value]
    );

    return (
        <MuiTimePicker
            {...rest}
            ref={ref}
            disabled={disabled || readOnly}
            // inputFormat={inputFormat}
            maxTime={DateHelper.from(maxTime).getObj() ?? undefined}
            minTime={DateHelper.from(minTime).getObj() ?? undefined}
            readOnly={readOnly}
            slotProps={{
                textField: {
                    disabled: disabled || readOnly,
                    error,
                    helperText,
                    hidden,
                    label,
                    name,
                    required,
                    size,
                    InputProps: {
                        readOnly: readOnly ?? undefined
                    },
                    inputProps: {
                        required: enableDOMAttributeRequired && required
                    }
                }
            }}
            value={DateHelper.from(value).getObj() ?? undefined}
            onError={handleError}
            onChange={
                onChange
                    ? (newValue) => onChange(newValue === null || !newValue.isValid() ? null : newValue)
                    : undefined
            }
        />
    );
});
